// Fees (SM-714, plan D62). Read-only for every role: the portal shows what
// applies — the assignment on the node's path, the plan, its current and
// scheduled versions, the node's share — and a calculator. Fee-plan and
// share-plan AUTHORING is the exchange's (backoffice) and, since SM-835,
// the root's OWNER and FINANCE (FEE_MANAGE) on Plan management
// (fees.plans.tsx): those members see a banner linking there, everyone
// else the read-only one. When no plan resolves, the server's missing_rule
// says why (SM-845); a schedule read the server refuses — a broken
// revenue-share version answers 422 BROKER_REVENUE_SHARE_INVALID (OFF_PATH,
// SERVICING_NAMED_TWICE) — shows that refusal, never an empty state. Every
// figure in the calculator is broker-service's preview, priced exactly as
// settlement prices a fill or a redemption: both components (exchange and
// broker) are always shown, and THIS node's share comes back from the
// server's split, never from browser arithmetic.
//
// fees.index.tsx, not fees.tsx: a fees.tsx route would be the parent of
// fees.plans.tsx, and /fees/plans would render this page.
//
//   GET  /api/broker/{id}/fees/schedule?currency=     StaffFeeScheduleResponse
//   POST /api/broker/{id}/fees/preview                FeePreviewRequest → FeePreviewResponse
//   POST /api/broker/{id}/fees/redemption-preview     RedemptionPreviewRequest → RedemptionPreviewResponse
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { feeManageRoot, treeRootOf } from "@/lib/capabilities";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Segmented, Select } from "@/components/ui/Field";
import { Money } from "@/components/ui/Money";
import { Id, KeyValues, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, ErrorState, QueryBlock } from "@/components/ui/States";
import { feePreview, fetchChildren, fetchFeeSchedule, keys, redemptionPreview } from "@/lib/queries";
import { formatBps, formatRate, parsePrice, parseQuantity } from "@/lib/units";
import {
  DEFAULT_REVENUE_SHARE_VERSION_ID,
  type FeePreviewResponse,
  type RedemptionPreviewResponse,
  type StaffFeeScheduleResponse,
} from "@/lib/types";
import { describeError } from "@/i18n/errorCopy";
import { useI18n } from "@/i18n/I18nProvider";

/** The refusal an order gets where no plan resolves; missing_rule is its details.rule. */
const FEE_MISSING_CODE = "BROKER_FEE_ASSIGNMENT_MISSING";

export const Route = createFileRoute("/_app/fees/")({
  beforeLoad: capabilityGuard("VIEW"),
  component: FeesPage,
});

function FeesPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const probe = { queryKey: keys.feeSchedule(nodeId, currency), queryFn: () => fetchFeeSchedule(nodeId, currency) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("fees.title")} subtitle={t("fees.subtitle")} coverage="node" money />
      <AuthoringBanner />
      <div className="space-y-4">
        <QueryBlock query={q} lines={5}>
          {(s) => <Schedule s={s} />}
        </QueryBlock>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <TradeCalculator />
          <RedemptionCalculator />
        </div>
      </div>
    </Guarded>
  );
}

/**
 * Who sets these plans. A member holding FEE_MANAGE on this tree's root
 * (its OWNER or FINANCE) gets a link to Plan management on that root —
 * from a sub-node too, since FEE_MANAGE is the root's own; everyone else
 * reads that the exchange or the root broker sets them.
 */
function AuthoringBanner() {
  const { t } = useI18n();
  const { staff, nodeId, path, currency } = useScope();
  const manage = feeManageRoot(staff, treeRootOf(staff, nodeId, path));
  if (!manage) return <p className="banner mb-4 text-xs">{t("fees.exchangeAuthored")}</p>;
  return (
    <div className="banner mb-4 text-xs" role="note">
      <span className="min-w-0 flex-1">{t("fees.rootManages", { root: manage.broker.display_name })}</span>
      <Link to="/fees/plans" search={{ node: manage.broker.broker_id, currency }} className="btn btn-ghost btn-sm">
        {t("fees.openPlanManagement")}
      </Link>
    </div>
  );
}

function Schedule({ s }: { s: StaffFeeScheduleResponse }) {
  const { t } = useI18n();
  if (!s.assignment || !s.current) {
    return (
      <div className="space-y-4">
        <NoPlan s={s} />
        {((s.upcoming ?? []).length > 0 || (s.upcoming_assignments ?? []).length > 0) && <Upcoming s={s} />}
      </div>
    );
  }
  const v = s.current;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <SectionCard title={t("fees.assigned")}>
        <KeyValues
          rows={[
            { label: t("fees.plan"), value: s.plan ? `${s.plan.name}` : "—" },
            {
              label: t("common.status"),
              value:
                s.plan?.retired_at_unix_ms && s.plan.retired_at_unix_ms <= s.resolved_at_unix_ms ? (
                  <Badge tone="warn">{t("fees.retired")}</Badge>
                ) : (
                  <Badge tone="yes">{t("fees.active")}</Badge>
                ),
            },
            { label: t("fees.version"), value: t("fees.versionN", { n: v.version }) },
            { label: t("fees.currency"), value: v.currency },
            { label: t("fees.effective"), value: <Time ms={v.effective_at_unix_ms} /> },
            { label: t("fees.assignment"), value: t(`feeScope.${s.assignment.scope}`) },
            { label: t("fees.assignmentTarget"), value: <Id value={s.assignment.target_broker_id} /> },
            { label: t("fees.assignedFrom"), value: <Time ms={s.assignment.effective_at_unix_ms} /> },
          ]}
        />
      </SectionCard>
      <SectionCard title={t("fees.rates")}>
        <KeyValues
          rows={[
            { label: t("fees.makerRate"), value: formatRate(v.maker_fee_rate, v.fee_rate_precision) },
            { label: t("fees.takerRate"), value: formatRate(v.taker_fee_rate, v.fee_rate_precision) },
            { label: t("fees.redemptionRate"), value: formatRate(v.redemption_fee_rate, v.fee_rate_precision) },
            { label: t("fees.nodeShare"), value: formatBps(s.node_share_bps) },
            {
              label: t("fees.shareVersion"),
              value:
                s.revenue_share_plan_version_id === DEFAULT_REVENUE_SHARE_VERSION_ID ? (
                  t("fees.defaultSplit")
                ) : (
                  <Id value={s.revenue_share_plan_version_id} />
                ),
            },
            { label: t("fees.planVersionId"), value: <Id value={v.plan_version_id} /> },
          ]}
        />
        <p className="mt-2 text-2xs text-t-3">{t("fees.shareNote")}</p>
      </SectionCard>
      <Upcoming s={s} className="lg:col-span-2" />
    </div>
  );
}

/**
 * No plan resolves for this node in this currency, and why: the server's
 * missing_rule (SM-837) is the rule an order here is refused with, read
 * through the catalog (an unknown rule shows raw). An older server sends
 * no rule: the plain empty state.
 */
function NoPlan({ s }: { s: StaffFeeScheduleResponse }) {
  const { t, locale } = useI18n();
  if (!s.missing_rule) return <EmptyState title={t("fees.noPlan", { currency: s.currency })} hint={t("fees.noPlanHint")} />;
  const why = describeError({ code: FEE_MISSING_CODE, details: { rule: s.missing_rule } }, locale);
  return (
    <div className="empty" role="status">
      <p className="text-sm font-semibold text-t-2">{t("fees.noPlan", { currency: s.currency })}</p>
      <p className="mt-2 text-sm text-t-1">{why.title}</p>
      {why.hint && <p className="mt-1 text-xs text-t-3">{why.hint}</p>}
      <p className="mt-3 flex flex-wrap justify-center gap-2">
        <span className="mono rounded bg-bg-3 px-1.5 py-0.5 text-2xs text-t-2">{FEE_MISSING_CODE}</span>
        <span className="mono rounded bg-bg-3 px-1.5 py-0.5 text-2xs text-t-2" title={t("error.rule")}>
          {s.missing_rule}
        </span>
      </p>
    </div>
  );
}

function Upcoming({ s, className }: { s: StaffFeeScheduleResponse; className?: string }) {
  const { t } = useI18n();
  return (
    <SectionCard title={t("fees.upcoming")} className={className}>
      {(s.upcoming ?? []).length === 0 && (s.upcoming_assignments ?? []).length === 0 ? (
        <p className="text-sm text-t-3">{t("fees.noUpcoming")}</p>
      ) : (
        <TableFrame label={t("fees.upcoming")}>
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{t("fees.effective")}</th>
                <th scope="col">{t("fees.change")}</th>
                <th scope="col">{t("fees.makerRate")}</th>
                <th scope="col">{t("fees.takerRate")}</th>
                <th scope="col">{t("fees.redemptionRate")}</th>
              </tr>
            </thead>
            <tbody>
              {(s.upcoming ?? []).map((u) => (
                <tr key={u.plan_version_id}>
                  <td className="text-xs">
                    <Time ms={u.effective_at_unix_ms} />
                  </td>
                  <td className="text-xs">{t("fees.versionN", { n: u.version })}</td>
                  <td className="num">{formatRate(u.maker_fee_rate, u.fee_rate_precision)}</td>
                  <td className="num">{formatRate(u.taker_fee_rate, u.fee_rate_precision)}</td>
                  <td className="num">{formatRate(u.redemption_fee_rate, u.fee_rate_precision)}</td>
                </tr>
              ))}
              {(s.upcoming_assignments ?? []).map((a) => (
                <tr key={a.assignment_id}>
                  <td className="text-xs">
                    <Time ms={a.effective_at_unix_ms} />
                  </td>
                  <td className="text-xs" colSpan={4}>
                    {a.plan_id
                      ? t("fees.upcomingAssignment", { scope: t(`feeScope.${a.scope}`), plan: a.plan_id })
                      : t("fees.upcomingCleared", { scope: t(`feeScope.${a.scope}`) })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableFrame>
      )}
    </SectionCard>
  );
}

/** The servicing node for a calculator: this node or one of its direct children, or a typed descendant id. */
function useServicingNode() {
  const { t } = useI18n();
  const { nodeId, node } = useScope();
  const params = { offset: 0, limit: 100 };
  const childrenQ = useQuery({ queryKey: keys.children(nodeId, params), queryFn: () => fetchChildren(nodeId, params) });
  const [picked, setPicked] = useState(nodeId);
  const [other, setOther] = useState("");
  const options = [
    { value: nodeId, label: t("fees.calc.thisNode", { name: node?.display_name ?? nodeId }) },
    ...(childrenQ.data?.items ?? []).map((c) => ({ value: c.broker_id, label: `${c.display_name} · ${c.broker_id}` })),
  ];
  const servicing = other.trim() || picked;
  const field = (
    <>
      <Select label={t("fees.calc.servicing")} value={picked} onChange={(e) => setPicked(e.target.value)} options={options} hint={t("fees.calc.servicingHint")} />
      <Input label={t("fees.calc.otherNode")} value={other} onChange={(e) => setOther(e.target.value)} placeholder={t("fees.calc.otherNodePlaceholder")} />
    </>
  );
  return { servicing, field };
}

function Row({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <tr>
      <th scope="row" className="font-medium text-t-2">
        {label}
        {sub && <span className="block text-2xs font-normal text-t-3">{sub}</span>}
      </th>
      <td className="text-right">{value}</td>
    </tr>
  );
}

function TradeCalculator() {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const { servicing, field } = useServicingNode();
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [role, setRole] = useState<"maker" | "taker">("taker");
  const [market, setMarket] = useState("");
  const [touched, setTouched] = useState(false);
  const pq = parseQuantity(qty);
  const pp = parsePrice(price);
  const mut = useMutation({
    mutationFn: () => {
      if (!pq.ok || !pp.ok) throw new Error("invalid input");
      return feePreview(nodeId, {
        broker_id: servicing,
        currency,
        price: pp.value,
        quantity: pq.value,
        price_precision: 4,
        amount_precision: pq.decimals,
        ...(market.trim() ? { market_id: market.trim() } : {}),
      });
    },
  });
  return (
    <SectionCard title={t("fees.calc.tradeTitle")}>
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (pq.ok && pp.ok) mut.mutate();
        }}
      >
        <Input
          label={t("fees.calc.quantity")}
          value={qty}
          inputMode="decimal"
          onChange={(e) => setQty(e.target.value)}
          error={touched && !pq.ok ? t(`fees.calc.inputError.${pq.error}`) : null}
          hint={t("fees.calc.quantityHint")}
        />
        <Input
          label={t("fees.calc.price")}
          value={price}
          inputMode="decimal"
          onChange={(e) => setPrice(e.target.value)}
          error={touched && !pp.ok ? t(`fees.calc.inputError.${pp.error}`) : null}
          hint={t("fees.calc.priceHint")}
        />
        <Segmented
          label={t("fees.calc.role")}
          value={role}
          options={[
            { value: "maker", label: t("revenueRole.maker") },
            { value: "taker", label: t("revenueRole.taker") },
          ]}
          onChange={setRole}
        />
        <Input label={t("fees.calc.market")} value={market} onChange={(e) => setMarket(e.target.value)} hint={t("fees.calc.marketHint")} />
        {field}
        <div className="flex items-end sm:col-span-2">
          <Button type="submit" loading={mut.isPending}>
            {t("fees.calc.run")}
          </Button>
        </div>
      </form>
      <ErrorState error={mut.error} className="mt-3" />
      {mut.data && <TradeResult r={mut.data} role={role} />}
    </SectionCard>
  );
}

function TradeResult({ r, role }: { r: FeePreviewResponse; role: "maker" | "taker" }) {
  const { t } = useI18n();
  // Fees are small: show the full public 4dp grid, not the currency's
  // everyday 2 decimals.
  const c = r.currency;
  const pick = (maker: string | undefined, taker: string | undefined) => (role === "maker" ? maker : taker);
  return (
    <div className="mt-4">
      <TableFrame label={t("fees.calc.tradeTitle")}>
        <table className="tbl">
          <tbody>
            <Row label={t("fees.calc.notional")} value={<Money dp={4} currency={c} value={r.notional} />} />
            <Row label={t("fees.calc.feeBase")} sub={t("fees.calc.feeBaseSub")} value={<Money dp={4} currency={c} value={r.fee_base} />} />
            <Row
              label={t("fees.calc.exchangeFee")}
              sub={
                r.exchange
                  ? t("fees.calc.atRate", { rate: formatRate(pick(r.exchange.maker_fee_rate, r.exchange.taker_fee_rate), r.exchange.fee_rate_precision) })
                  : t("fees.calc.exchangeNeedsMarket")
              }
              value={r.exchange ? <Money dp={4} currency={c} value={pick(r.exchange.maker_fee, r.exchange.taker_fee)} /> : "—"}
            />
            <Row
              label={t("fees.calc.brokerCommission")}
              sub={t("fees.calc.atRate", { rate: formatRate(pick(r.broker.maker_fee_rate, r.broker.taker_fee_rate), r.broker.fee_rate_precision) })}
              value={<Money dp={4} currency={c} value={pick(r.broker.maker_fee, r.broker.taker_fee)} />}
            />
            <Row label={t("fees.calc.totalUserFee")} value={<Money dp={4} currency={c} value={pick(r.total_maker_fee, r.total_taker_fee)} />} />
            <Row
              label={t("fees.calc.nodeShare")}
              sub={r.node_share ? t("fees.calc.shareOf", { share: formatBps(r.node_share.share_bps) }) : t("fees.calc.noShare")}
              value={r.node_share ? <Money dp={4} currency={c} value={pick(r.node_share.maker_amount, r.node_share.taker_amount)} /> : "—"}
            />
          </tbody>
        </table>
      </TableFrame>
      <p className="mt-2 text-2xs text-t-3">
        {t("fees.calc.lockedPlan")} <Id value={r.fee_plan_version_id} /> · {t("fees.calc.feeOwner")} <Id value={r.fee_owner_broker_id} />
      </p>
    </div>
  );
}

function RedemptionCalculator() {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const { servicing, field } = useServicingNode();
  const [qty, setQty] = useState("");
  const [market, setMarket] = useState("");
  const [touched, setTouched] = useState(false);
  const pq = parseQuantity(qty);
  const mut = useMutation({
    mutationFn: () => {
      if (!pq.ok) throw new Error("invalid input");
      return redemptionPreview(nodeId, {
        broker_id: servicing,
        currency,
        quantity: pq.value,
        amount_precision: pq.decimals,
        ...(market.trim() ? { market_id: market.trim() } : {}),
      });
    },
  });
  return (
    <SectionCard title={t("fees.calc.redemptionTitle")}>
      {currency !== "GOLD" && <p className="mb-3 text-xs text-t-3">{t("fees.calc.redemptionGoldOnly")}</p>}
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (pq.ok) mut.mutate();
        }}
      >
        <Input
          label={t("fees.calc.winningShares")}
          value={qty}
          inputMode="decimal"
          onChange={(e) => setQty(e.target.value)}
          error={touched && !pq.ok ? t(`fees.calc.inputError.${pq.error}`) : null}
        />
        <Input label={t("fees.calc.market")} value={market} onChange={(e) => setMarket(e.target.value)} hint={t("fees.calc.marketHint")} />
        {field}
        <div className="flex items-end sm:col-span-2">
          <Button type="submit" loading={mut.isPending}>
            {t("fees.calc.run")}
          </Button>
        </div>
      </form>
      <ErrorState error={mut.error} className="mt-3" />
      {mut.data && <RedemptionResult r={mut.data} />}
    </SectionCard>
  );
}

function RedemptionResult({ r }: { r: RedemptionPreviewResponse }) {
  const { t } = useI18n();
  const c = r.currency;
  return (
    <div className="mt-4">
      <TableFrame label={t("fees.calc.redemptionTitle")}>
        <table className="tbl">
          <tbody>
            <Row label={t("fees.calc.gross")} value={<Money dp={4} currency={c} value={r.gross} />} />
            <Row
              label={t("fees.calc.exchangeRedemptionFee")}
              sub={
                r.exchange_fee !== undefined && r.exchange_fee_rate
                  ? t("fees.calc.atRate", { rate: formatRate(r.exchange_fee_rate, r.exchange_fee_rate_precision ?? 6) })
                  : t("fees.calc.exchangeNeedsMarket")
              }
              value={r.exchange_fee !== undefined ? <Money dp={4} currency={c} value={r.exchange_fee} /> : "—"}
            />
            <Row
              label={t("fees.calc.brokerRedemptionFee")}
              sub={t("fees.calc.atRate", { rate: formatRate(r.broker_fee_rate, r.broker_fee_rate_precision) })}
              value={<Money dp={4} currency={c} value={r.broker_fee} />}
            />
            <Row label={t("fees.calc.totalFee")} value={<Money dp={4} currency={c} value={r.total_fee} />} />
            <Row label={t("fees.calc.netPayout")} value={<Money dp={4} currency={c} value={r.net_payout} />} />
            <Row
              label={t("fees.calc.nodeShare")}
              sub={r.node_share ? t("fees.calc.shareOf", { share: formatBps(r.node_share.share_bps) }) : t("fees.calc.noShare")}
              value={r.node_share ? <Money dp={4} currency={c} value={r.node_share.amount} /> : "—"}
            />
          </tbody>
        </table>
      </TableFrame>
      <p className="mt-2 text-2xs text-t-3">
        {t("fees.calc.lockedPlan")} <Id value={r.fee_plan_version_id} /> · {t("fees.calc.feeOwner")} <Id value={r.fee_owner_broker_id} />
      </p>
    </div>
  );
}
